import * as _ from 'lodash';
import { useState } from 'react';
import type { VoidFunctionComponent } from 'react';

import { ErrorAlert } from '../../../generic';
import Loader from '../../../generic/Loader';
import Header from '../../../shared/settings/SettingsPageHeader';
import SettingsPageTableActions from '../../../shared/settings/SettingsPageTableActions';
import type { APITokenResponse } from '../../../../../shared/settings/apiTokens';

import APITokenTable from './APITokenTable';
import ModalCreateOrEditToken from './ModalCreateOrEditToken';
import useAPITokens from './useAPITokens';
import type { ModalMode } from './ModalCreateOrEditToken';

type TokenBodyProps = {
  tokens: APITokenResponse[];
  onDeleteToken: (id: number) => void;
  onEditToken: (tokenId: number) => void;
};

const APITokenBody: VoidFunctionComponent<TokenBodyProps> = ({
  tokens,
  onDeleteToken,
  onEditToken,
}) => {
  if (_.isEmpty(tokens)) {
    return (
      <div className="service-connected-container">
        <h4 className="text-center">No API tokens found.</h4>
      </div>
    );
  }

  return (
    <APITokenTable
      tokens={tokens}
      onDeleteToken={onDeleteToken}
      onEditToken={onEditToken}
    />
  );
};

const APITokenSettings: VoidFunctionComponent = () => {
  const {
    apiTokens: allTokens,
    loadingState: {
      isReading: isLoading,
      isMutating,
    },
    errorState: {
      readErrors,
      mutateErrors,
    },
    onCreateToken,
    onUpdateToken,
    onDeleteToken,
  } = useAPITokens();

  const [modalMode, setModalMode] = useState<ModalMode>({
    mode: 'Hide',
    token: null,
  });

  const [searchValue, setSearchValue] = useState<string>('');

  // Should this logic live in the hook?
  // Filter tokens by search term if one is entered
  const apiTokens = allTokens.filter(token => {
    if (!searchValue) {
      return true;
    }

    return (
      (token.name && token.name.includes(searchValue)) ||
      (token.username && token.username.includes(searchValue)) ||
      (token.email && token.email.includes(searchValue))
    );
  });

  const onSetTokenToEdit = (id: number) => {
    const nextToken = apiTokens.find(t => t.id === id);
    if (nextToken) {
      setModalMode({
        token: nextToken,
        mode: 'Edit',
      });
    }
  };

  const onHideModal = () => {
    setModalMode({
      mode: 'Hide',
      token: null,
    });
  };

  const onAddToken = () => {
    setModalMode({
      mode: 'Create',
      token: null,
    });
  };

  return (
    <>
      <Header heading="API Tokens">
        <Header.Paragraph>
          Tokens you have generated that can be used to access the FOSSA API.
          Apps using these tokens impersonate your identity.
        </Header.Paragraph>
        <Header.Paragraph>
          Push-Only tokens can only be used to send data to FOSSA. This allows
          your CI to safely expose them to 3rd parties.
        </Header.Paragraph>
      </Header>
      <SettingsPageTableActions>
        <SettingsPageTableActions.TooltipButton
          id="add-token-button"
          size="large"
          type="secondary"
          onClick={onAddToken}
          tooltip="Add a new token"
          showTooltip={false}
        >
          Add New Token
        </SettingsPageTableActions.TooltipButton>
        <SettingsPageTableActions.SearchInput
          value={searchValue}
          onChange={setSearchValue}
          placeholder="Search by token name or User"
          label="search-by-token"
        />
      </SettingsPageTableActions>
      <div>
        {!_.isEmpty(readErrors) && <ErrorAlert status={{ errors: readErrors }} />}
        {!_.isEmpty(mutateErrors) && <ErrorAlert status={{ errors: mutateErrors }} />}
        {isLoading
          ? <Loader />
          : <APITokenBody
              onDeleteToken={onDeleteToken}
              onEditToken={onSetTokenToEdit}
              tokens={apiTokens}
            />}
      </div>
      <ModalCreateOrEditToken
        mode={modalMode}
        isLoading={isMutating}
        errors={mutateErrors}
        onHide={onHideModal}
        onUpdateToken={onUpdateToken}
        onCreateToken={onCreateToken}
      />
    </>
  );
};

export default APITokenSettings;
